import { useCallback, useEffect, useMemo, useState } from 'react';

import type { CareerFeedAssistantQuestionSuggestion } from './CareerFeedAssistantQuestions';

type UseSuggestionsType = {
  multiSelectSuggestions: CareerFeedAssistantQuestionSuggestion[];
  otherOption: CareerFeedAssistantQuestionSuggestion[];
  skipOption: CareerFeedAssistantQuestionSuggestion[];
};
export const useSuggestions = (
  suggestions: CareerFeedAssistantQuestionSuggestion[],
) => {
  const {
    multiSelectSuggestions: _multiSelectSuggestions,
    otherOption: _otherOption,
    skipOption: _skipOption,
  } = useMemo(
    () =>
      suggestions.reduce<UseSuggestionsType>(
        (acc, suggestion) => {
          if (suggestion.showFreeTextField) {
            acc.otherOption.push(suggestion);
          } else if (suggestion.isSkipButton) {
            acc.skipOption.push(suggestion);
          } else {
            acc.multiSelectSuggestions.push(suggestion);
          }
          return acc;
        },
        {
          multiSelectSuggestions: [],
          otherOption: [],
          skipOption: [],
        },
      ),
    [suggestions],
  );

  const _singleOption = useMemo(
    () => [..._otherOption, ..._skipOption],
    [_otherOption, _skipOption],
  );

  const multiSelectSuggestions = useMultiSelectOptions(_multiSelectSuggestions);
  const singleOptions = useSingleSelectOptions(_singleOption);

  return useMemo(
    () => ({
      suggestions: [
        ...multiSelectSuggestions.options.map((option) => ({
          ...option,
          onToggleSelect: () => {
            option.onToggleSelect();
            singleOptions.clearSelected();
          },
          type: 'multiSelect',
        })),
        ...singleOptions.options.map((option) => ({
          ...option,
          onToggleSelect: () => {
            option.onToggleSelect();
            multiSelectSuggestions.clearSelected();
          },
          type: 'singleSelect',
        })),
      ],
      selectedSuggestions: [
        ...multiSelectSuggestions.options,
        ...singleOptions.options,
      ]
        .filter((option) => option.isSelected)
        .map((option) => option.data),
    }),
    [multiSelectSuggestions, singleOptions],
  );
};

export interface OptionsType<T> {
  options: Array<{
    data: T;
    isSelected: boolean;
    onToggleSelect: () => void;
  }>;
  clearSelected: () => void;
}

const useMultiSelectOptions = <T>(options: T[]): OptionsType<T> => {
  const [selected, setSelected] = useState(
    new Array(options.length).fill(false),
  );

  const toggleSelected = useCallback((index: number) => {
    setSelected((prev) => {
      const newSelected = [...prev];
      newSelected[index] = !newSelected[index];
      return newSelected;
    });
  }, []);

  const clearSelected = useCallback(() => {
    setSelected((prev) =>
      prev.some(Boolean) ? new Array(options.length).fill(false) : prev,
    );
  }, [options.length]);

  // Should reset all selected suggestions when suggestions change
  useEffect(() => clearSelected(), [clearSelected, options]);

  return useMemo(
    () => ({
      options: options.map((suggestion, index) => ({
        data: suggestion,
        onToggleSelect: () => toggleSelected(index),
        isSelected: selected[index],
      })),
      clearSelected,
    }),
    [options, clearSelected, selected, toggleSelected],
  );
};

const useSingleSelectOptions = <T>(options: T[]): OptionsType<T> => {
  const [selected, setSelected] = useState<number | null>(null);

  const toggleSelected = useCallback((index: number) => {
    setSelected((prev) => (prev === null || prev !== index ? index : null));
  }, []);

  const clearSelected = useCallback(() => setSelected(null), []);

  // Should reset selected suggestions when suggestions change
  useEffect(() => clearSelected(), [clearSelected, options]);

  return useMemo(
    () => ({
      options: options.map((suggestion, index) => ({
        data: suggestion,
        onToggleSelect: () => toggleSelected(index),
        isSelected: selected === index,
      })),
      clearSelected,
    }),
    [clearSelected, selected, options, toggleSelected],
  );
};
